import { FC } from 'react';
import PostComposerContentText from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentText';
import { PostComposerContentProps } from 'app/modules/posts/modules/post/components/composer/PostComposerContent';
import PostComposerContentMediaAttachments from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentMediaAttachemnts';
import { PseudoPostType } from 'constants/post';
import PostComposerContentLinkAttachments from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentLinkAttachments';
import PostsConfig from 'app/config/posts';
import PostComposerContentFirstComment from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentFirstComment';
import PostComposerContentLinkAppender from 'app/modules/posts/modules/post/components/composer/postComposerContent/PostComposerContentLinkAppender';
import { TYPE_VIDEO } from '@kontentino/kontentino-constants/Posts';
import { VIDEO_STATUS_AVAILABLE } from '@kontentino/kontentino-constants/Video';
import { Field, TextInput } from '@kontentino/ui';
import { useTranslation } from 'react-i18next';
import { getSourcesForPseudoType } from 'app/modules/posts/modules/post/utils/fileSourceConfig';
import { isDocumentAttachmentCard } from 'utils/attachments';

type Props = PostComposerContentProps;

const PostComposerContentLinkedin: FC<Props> = ({
  value,
  onChange,
  options,
  onOptionsChange,
  onTextEditorInit,
  onTranslationEditorInit,
  swapTextTranslation,
}) => {
  const { t } = useTranslation();
  const pseudoType = value.pseudoType;
  const documentCard = value.attachments.cards.find(isDocumentAttachmentCard);
  const canSetupVideoThumbnail =
    pseudoType === PseudoPostType.Video &&
    value.attachments.cards.length === 1 &&
    value.type === TYPE_VIDEO &&
    value.attachments.cards[0]?.medium?.type === 'video' &&
    value.attachments.cards[0]?.medium?.status === VIDEO_STATUS_AVAILABLE;

  return (
    <>
      <PostComposerContentText
        value={value}
        onChange={onChange}
        onTextEditorInit={onTextEditorInit}
        onTranslationEditorInit={onTranslationEditorInit}
        swapTextTranslation={swapTextTranslation}
      />

      {pseudoType === PseudoPostType.Document && (
        <>
          {documentCard && (
            <Field.Group data-cy="post-composer_linkedin-document-title-group">
              <Field.Label required>{t('title')}</Field.Label>
              <TextInput
                data-cy="post-composer_linkedin-document-title"
                maxLength={255}
                value={documentCard.title || documentCard.asset.file_name}
                onChange={(e) => {
                  const title = e.target.value;
                  onChange((prev) => ({
                    ...prev,
                    attachments: {
                      ...prev.attachments,
                      cards: prev.attachments.cards.map((card) =>
                        isDocumentAttachmentCard(card)
                          ? { ...card, title }
                          : card,
                      ),
                    },
                  }));
                }}
              />
            </Field.Group>
          )}
          <PostComposerContentMediaAttachments
            value={value}
            onChange={onChange}
            allowedFiles={['document']}
            maxCount={PostsConfig.MIN_FILES_TO_ACCEPT}
            sources={getSourcesForPseudoType(pseudoType)}
          />
        </>
      )}

      <PostComposerContentFirstComment onChange={onChange} value={value} />
      {pseudoType && (
        <>
          {[PseudoPostType.Video, PseudoPostType.PhotoGallery].includes(
            pseudoType,
          ) && (
            <PostComposerContentMediaAttachments
              value={value}
              onChange={onChange}
              canSetupVideoThumbnail={canSetupVideoThumbnail}
              allowedFiles={
                PseudoPostType.Video === pseudoType ? ['video'] : ['image']
              }
              maxCount={
                [PseudoPostType.Photo, PseudoPostType.Video].includes(
                  pseudoType,
                )
                  ? PostsConfig.MIN_FILES_TO_ACCEPT
                  : PostsConfig.MAX_FILES_TO_ACCEPT
              }
            />
          )}

          {[PseudoPostType.Link, PseudoPostType.Carousel].includes(
            pseudoType,
          ) && (
            <PostComposerContentLinkAttachments
              value={value}
              onChange={onChange}
              onOptionsChange={onOptionsChange}
              withLinkOwnership={false}
              withDescriptionField={false}
              withMultiShareEndCard={false}
              withCallToAction={false}
              maxCount={
                pseudoType === PseudoPostType.Link
                  ? PostsConfig.MIN_FILES_TO_ACCEPT
                  : PostsConfig.MAX_FILES_TO_ACCEPT
              }
            />
          )}
          {options.canAppendLinkFromText &&
            [PseudoPostType.Link, PseudoPostType.Carousel].includes(
              pseudoType,
            ) && (
              <PostComposerContentLinkAppender
                value={value}
                onChange={onChange}
              />
            )}
        </>
      )}
    </>
  );
};

export default PostComposerContentLinkedin;
